import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@cwos/api-client';
import { SkeletonSet, SuccessEmpty } from '@cwos/ui-admin';
import { useNav } from '../navigation.js';
import { adminRouteFor, groupByDay, relativeAt } from './notificationsLogic.js';
import { LoadFailure } from '../LoadFailure.js';

/**
 * Notification Centre — what Phibian has told you.
 *
 * Deliberately not the Needs Attention inbox and deliberately not Messages.
 * Phibian has three kinds of attention and a person can only triage them if the
 * product keeps them apart:
 *
 *   Messages        a person wants to talk to you
 *   Notifications   Phibian is telling you something happened   ← this screen
 *   Needs attention something operational requires action
 *
 * Nothing technical appears here. A rule id, a retry count or a provider
 * response belongs in internal observability, not on a supervisor's screen.
 */
export function NotificationCenter() {
  const qc = useQueryClient();
  const navigate = useNav();

  const list = useQuery({
    queryKey: ['notifications', 'list'],
    queryFn: () => notifications.list({ limit: 100 }),
  });

  const markRead = useMutation({
    mutationFn: (id: string) => notifications.markRead(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const markAll = useMutation({
    mutationFn: () => notifications.markAllRead(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const items = list.data?.notifications ?? [];
  const groups = groupByDay(items);

  /**
   * Opening a notification marks it read and follows its target.
   *
   * A target with no Admin destination still marks read — the person has seen
   * it — but navigates nowhere rather than to a dead page.
   */
  const open = (notification: notifications.Notification) => {
    if (!notification.read) markRead.mutate(notification.id);
    const route = adminRouteFor(notification.target);
    if (route) navigate(route);
  };

  return (
    <div className="rn-col" style={{ maxWidth: 760 }}>
      <div className="rn-card-head" style={{ marginBottom: 'var(--space-12)' }}>
        <div>
          <h1 className="rn-card-title">Notifications</h1>
          <p className="rn-muted" style={{ marginTop: 'var(--space-6)' }}>
            What Phibian has told you. Conversations live in Messages; things needing action live in
            Needs attention.
          </p>
        </div>
        {(list.data?.unread ?? 0) > 0 ? (
          <button
            type="button"
            className="rn-link"
            disabled={markAll.isPending}
            onClick={() => markAll.mutate()}
          >
            {markAll.isPending ? 'Marking…' : 'Mark all read'}
          </button>
        ) : null}
      </div>

      {list.isLoading ? (
        <SkeletonSet surface="table" rows={6} />
      ) : list.isError && !list.data ? (
        <LoadFailure
          error={list.error}
          message="We couldn’t load your notifications just now."
          onRetry={() => void list.refetch()}
        />
      ) : items.length === 0 ? (
        <SuccessEmpty
          heading="Nothing to catch up on"
          message="Phibian will tell you here when something changes."
        />
      ) : (
        groups.map((group) => (
          <section key={group.heading} style={{ marginBottom: 'var(--space-16)' }}>
            <h2 className="rn-muted" style={{ marginBottom: 'var(--space-8)' }}>
              {group.heading}
            </h2>
            <ul style={{ display: 'grid', gap: 'var(--space-8)' }}>
              {group.items.map((item) => (
                <li key={item.id}>
                  <button
                    type="button"
                    className="rn-card"
                    onClick={() => open(item)}
                    style={{
                      display: 'block',
                      width: '100%',
                      textAlign: 'left',
                      cursor: 'pointer',
                      font: 'inherit',
                      color: 'inherit',
                      border: 0,
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'baseline',
                        gap: 'var(--space-8)',
                      }}
                    >
                      {/* Unread is carried by the word, not only by the dot —
                          colour alone is not an accessible status. */}
                      <span
                        aria-hidden="true"
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          background: item.read ? 'transparent' : 'var(--color-sky)',
                          flexShrink: 0,
                        }}
                      />
                      <span style={{ fontWeight: item.read ? 400 : 700 }}>
                        {item.title}
                        <span className="rn-sr-only">{item.read ? ' (read)' : ' (unread)'}</span>
                      </span>
                      <span className="rn-muted" style={{ marginLeft: 'auto' }}>
                        {relativeAt(item.occurredAt)}
                      </span>
                    </div>
                    {item.body ? (
                      <p className="rn-muted" style={{ marginTop: 'var(--space-6)' }}>
                        {item.body}
                      </p>
                    ) : null}
                    <p className="rn-muted" style={{ marginTop: 'var(--space-6)' }}>
                      {item.categoryLabel}
                    </p>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ))
      )}
    </div>
  );
}
